import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Listbox,
  type ListboxItem,
  type ListboxProps
} from '@bliks-io/ui';

export interface TransferListboxProps<T extends ListboxItem> extends Omit<
  ListboxProps<T>,
  'className'
> {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Additional class for Listbox component
   */
  listboxClassName?: string;
}

/**
 * Reusable form field component combining Field + Listbox
 * Can be used standalone or wrapped by RHF adapter components
 */
export function TransferListbox<T extends ListboxItem>({
  label,
  description,
  error,
  required = false,
  id,
  name,
  orientation = 'vertical',
  fieldClassName,
  listboxClassName,
  ...listboxProps
}: TransferListboxProps<T>) {
  const fieldId = id || name;
  const hasError = !!error;

  return (
    <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
      {label && (
        <FieldLabel htmlFor={fieldId}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </FieldLabel>
      )}
      <FieldContent>
        {description && <FieldDescription>{description}</FieldDescription>}
        <Listbox {...listboxProps} className={listboxClassName} />
        {error && <FieldError>{error}</FieldError>}
      </FieldContent>
    </Field>
  );
}

TransferListbox.displayName = 'TransferListbox';
